import { useMemo, useState } from 'react';
import { Button, createListCollection, Dialog, HStack, Input, Portal, Select, VStack } from '@chakra-ui/react';
import type { TaxonomyCategoryOut, TaxonomyRoleOut } from '@/client';
import { Field } from '@/components/ui/field';
import { getInputStyles, SELECT_TRIGGER_PROPS } from '@/components/ui/inputStyles';
import { dropdownScrollbarCss } from '@/components/ui/scrollbar';
import { useCreateContactRole } from '@/hooks/service-providers/useCreateContactRole';
import { orderRoleGroups } from '@/components/services/role/roleGroupOrder';

/** Where a new role goes unless the reader moves it: most roles a property is missing are a trade. */
const DEFAULT_GROUP_CODE = 'trades_services';

const INPUT_STYLES = getInputStyles();

interface CreateRoleDialogProps {
	houseId: number;
	/** The catalog's groups, which are the only places a role can go. */
	groups: readonly TaxonomyCategoryOut[];
	/** The name to start from, which is what the reader typed; null while closed. */
	draft: string | null;
	/** The role created, or null when the reader backed out. */
	onDone: (role: TaxonomyRoleOut | null) => void;
}

/**
 * Names a custom role and files it under a group.
 *
 * There is no `<form>` here: the picker opening this sits inside the contact form, and React
 * bubbles a portalled form's submit to that one.
 */
const CreateRoleDialog = (props: CreateRoleDialogProps) => {
	const { houseId, groups, draft, onDone } = props;
	const createRole = useCreateContactRole(houseId);
	const groupCollection = useMemo(
		() =>
			createListCollection({
				items: orderRoleGroups(groups).map((group) => ({ value: group.code, label: group.label })),
			}),
		[groups],
	);
	const defaultGroupCode = groups.some((group) => group.code === DEFAULT_GROUP_CODE)
		? DEFAULT_GROUP_CODE
		: (orderRoleGroups(groups)[0]?.code ?? '');

	// Re-seeded whenever a new draft opens the dialog, so a second create does not start from the first.
	const [openedDraft, setOpenedDraft] = useState(draft);
	const [label, setLabel] = useState(draft ?? '');
	const [groupCode, setGroupCode] = useState(defaultGroupCode);
	if (draft !== openedDraft) {
		setOpenedDraft(draft);
		setLabel(draft ?? '');
		setGroupCode(defaultGroupCode);
	}

	const canCreate = !!label.trim() && !!groupCode && !createRole.isPending;
	const create = async () => {
		if (!canCreate) return;
		try {
			const role = await createRole.mutateAsync({ label: label.trim(), category_code: groupCode });
			onDone(role);
		} catch {
			// The mutation owns its error notice; the dialog stays open to try again.
		}
	};

	return (
		<Dialog.Root
			open={draft !== null}
			onOpenChange={({ open }) => {
				if (!open) onDone(null);
			}}
			size="sm"
			placement="center"
			motionPreset="slide-in-bottom"
		>
			<Portal>
				<Dialog.Backdrop />
				<Dialog.Positioner>
					<Dialog.Content borderRadius="20px" maxW="420px">
						<Dialog.Header pb={0}>
							<Dialog.Title>Create a custom service</Dialog.Title>
						</Dialog.Header>
						<Dialog.Body pt={4} pb={0}>
							<VStack gap={4} align="stretch">
								<Field label="Category" required>
									<Select.Root
										collection={groupCollection}
										value={groupCode ? [groupCode] : []}
										onValueChange={(details) => setGroupCode(details.value[0] ?? '')}
										positioning={{ strategy: 'fixed', hideWhenDetached: true }}
										// `Field` paints its input pill onto its child; the trigger is the pill here.
										border="none"
										bg="transparent"
										h="auto"
										px={0}
										borderRadius={0}
										boxShadow="none"
									>
										<Select.HiddenSelect />
										<Select.Control>
											<Select.Trigger {...SELECT_TRIGGER_PROPS} h={12} textStyle="body">
												<Select.ValueText placeholder="Choose a category" />
											</Select.Trigger>
											<Select.IndicatorGroup pe={4}>
												<Select.Indicator color="brand.fg" />
											</Select.IndicatorGroup>
										</Select.Control>
										<Select.Positioner>
											<Select.Content
												minW="var(--reference-width)"
												borderRadius="2xl"
												p={2}
												css={dropdownScrollbarCss}
											>
												{groupCollection.items.map((item) => (
													<Select.Item
														item={item}
														key={item.value}
														borderRadius="xl"
														textStyle="body"
													>
														{item.label}
														<Select.ItemIndicator />
													</Select.Item>
												))}
											</Select.Content>
										</Select.Positioner>
									</Select.Root>
								</Field>
								<Field label="Service name" required>
									<Input
										{...INPUT_STYLES}
										fontSize="md"
										value={label}
										onChange={(event) => setLabel(event.currentTarget.value)}
										onKeyDown={(event) => {
											if (event.key !== 'Enter') return;
											event.preventDefault();
											void create();
										}}
									/>
								</Field>
							</VStack>
						</Dialog.Body>
						<Dialog.Footer pt={4}>
							<HStack gap={2} w="full">
								<Button
									type="button"
									flex="1"
									variant="outline"
									borderColor="border.default"
									borderRadius="full"
									onClick={() => onDone(null)}
								>
									Cancel
								</Button>
								<Button
									type="button"
									flex="1"
									bg="brand.solid"
									color="brand.contrast"
									borderRadius="full"
									disabled={!canCreate}
									loading={createRole.isPending}
									onClick={() => void create()}
								>
									Create
								</Button>
							</HStack>
						</Dialog.Footer>
					</Dialog.Content>
				</Dialog.Positioner>
			</Portal>
		</Dialog.Root>
	);
};

export default CreateRoleDialog;
